import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { Document, DocumentWithChunks } from '@/types/documents.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';

export interface ListDocumentsParams {
  page: number;
  limit: number;
  /** The organisation whose documents to list; omitted lists every one they may see. */
  organisationId?: string;
  /** Narrows by file name, server-side. */
  query?: string;
  /** Without one the server answers newest first. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * A page of documents.
 *
 * The one document list in the console: the documents page reads it whole, and
 * the track and cohort tabs read it scoped to one organisation and filtered by
 * what was typed. They are the same endpoint, so they are the same function.
 */
export async function listDocuments({
  page,
  limit,
  organisationId,
  query,
  sortBy,
  sortOrder,
}: ListDocumentsParams): Promise<PaginatedResponse<Document>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });

  if (organisationId) {
    params.append('organisation_id', organisationId);
  }
  if (query) {
    params.append('query', query);
  }
  if (sortBy) {
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder ?? 'asc');
  }

  return await apiFetchJson<PaginatedResponse<Document>>(
    `/documents?${params.toString()}`,
  );
}

/** The document with the chunks it was split into — what the reader inspects. */
export async function getDocument(id: string): Promise<DocumentWithChunks> {
  return await apiFetchJson<DocumentWithChunks>(`/documents/${id}`);
}

/** A short-lived link to the stored file. Handed out, never followed here. */
export async function getDocumentDownloadUrl(id: string): Promise<string> {
  const { url } = await apiFetchJson<{ url: string }>(`/documents/${id}/download`);
  return url;
}

/**
 * Upload one document. Multipart, field `file`, content type left to the
 * browser so the boundary is right.
 */
export async function uploadDocument(
  file: File,
  organisationId?: string,
): Promise<Document> {
  const body = new FormData();
  body.append('file', file);

  const query = organisationId ? `?organisation_id=${organisationId}` : '';

  return await apiFetchJson<Document>(`/documents${query}`, { method: 'POST', body });
}

/** Answers 204 with no body. */
export async function deleteDocument(id: string): Promise<void> {
  await apiFetch(`/documents/${id}`, { method: 'DELETE' });
}
